/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import _ from 'lodash';
import emitter from 'tiny-emitter/instance';
import queryClient from '@/queries/queryClient.js';
import { queries } from '@/queries/index.js';
import { transform, filterData } from '../project/transforms.js';
import updater from '../project/updater.js';
import { EVENT } from '../../constants.js';
import requests from '../requests.js';

/**
 * The application channel prefixes
 */
const types = Object.freeze({
  PROJECT_UPDATE: 'Project.Update',
  PROJECT_COLLABORATE: 'Project.Collaborate',
  USER_UPDATE: 'User.Update',
  WORKSPACE_UPDATE: 'Workspace.Update',
});

/**
 *  A store for managing the application Echo channels. Non persisting store.
 */
const state = {
  /**
   * The channel names are stored since we do not want to have the same channel twice.
   */
  channels: [],

  /**
   * The open project collaborators (online participants)
   */
  collaborators: [],
};

const getters = {
  /**
   * Return whether the user already subscribed to the given channel
   * @returns {Object}
   */
  getIsJoined(state) {
    return name => state.channels.includes(name);
  },

  /**
   * Return the collaborators (online participants) on the open project
   * @returns {Array}
   */
  getCollaborators(state) {
    return state.collaborators;
  },

  /**
   * Return one specific collaborator by id
   * @param   {Number}  id  The collaborator id
   * @returns {Object}
   */
  getCollaborator(state) {
    return id => {
      return state.collaborators.find(c => c.id === id) || null;
    };
  },
};

const actions = {
  /**
   * Activate the project channels
   */
  startProjectChannels(context) {
    const hash = context.rootGetters['project/getHash'];
    const board = context.rootGetters['project/config/boards/getOpen'];

    context.dispatch('startProjectUpdateChannel').then(() => {
      context.dispatch('startProjectCollaborateChannel').then(() => {
        context.dispatch('whisperBoard', {
          hash,
          board,
        });
        context.dispatch('whisperCursor', {
          hash,
          pos: { x: 3000, y: 3000 },
        });
      });
    });
  },

  /**
   * Leave the project channels
   * @param {string}  hash  The project to leave
   */
  leaveProjectChannels(context, { hash }) {
    context.dispatch('leaveProjectUpdateChannel', { hash });
    context.dispatch('leaveProjectCollaborateChannel', { hash });
  },

  /**
   * Activate the project update channel, a private (for private projects) or public (for public projects) channel.
   * This channel receives data of project updates.
   * @param {string}  hash  The project hash
   */
  startProjectUpdateChannel(context) {
    console.log('Project update channel started');
    const hash = context.rootGetters['project/getHash'];
    const inPrototype = // To avoid cyclic import of router
      window.location.pathname.match(/\/app\/project\/.*\/play/g)?.[0] ===
      window.location.pathname;

    const channelName = `${types.PROJECT_UPDATE}.${hash}`;
    // If already joined do nothing
    if (context.getters.getIsJoined(channelName)) return;

    const channel = context.rootGetters['project/getPublicLink']
      ? window.echo.channel(channelName)
      : window.echo.private(channelName);

    function updateURLQueries(board, { scale, x, y }) {
      window.history.replaceState(
        {},
        null,
        `${window.location.pathname}?board=${board}&scale=${parseFloat(
          scale
        ).toFixed(6)}&coords=${x},${y}`
      );
    }
    channel
      .listen('ProjectUpdated', e => {
        console.log('ProjectUpdated broadcast received for a project:', e);
        const { patch, userId, version } = e;
        // Get my user id
        const myId = context.rootGetters['user/account/getId'];
        const isSameUser = myId && userId === myId;
        // Store the version
        updater.setVersion(version);

        const project = {
          data: _.cloneDeep(context.rootGetters['project/getData']),
          config: _.cloneDeep(context.rootGetters['project/getConfig']),
        };
        const openBoard = project.config?.boards?.open;
        if (
          openBoard &&
          !inPrototype &&
          project.config?.boards?.byId[openBoard]?.transform
        ) {
          updateURLQueries(
            openBoard,
            project.config.boards.byId[openBoard].transform
          );
        }
        // In case the user is not the same filter out any config operations
        const tPatch = transform(
          isSameUser ? patch : filterData(patch),
          project
        );
        tPatch.forEach(p => {
          if (
            p.op === 'replace' &&
            p.path === `/config/boards/byId/${openBoard}/transform`
          ) {
            emitter.emit(EVENT.PANZOOMTOTRANSFORM, p.value, {}, true);
          }
        });
        updater.add(tPatch);
        updater.complete({ apply: true, save: false, undoable: isSameUser });
      })
      .listen('ProjectMetadataUpdated', e => {
        const { name, publicLink } = e;
        context.commit('project/setName', { value: name }, { root: true });
        context.commit(
          'project/setPublicLink',
          { value: publicLink },
          { root: true }
        );
      })
      .listen('ProjectExternalUsersUpdated', e => {
        const { user_id: userId } = e;
        console.log(e);
        queryClient.invalidateQueries({
          queryKey: queries.project.externalUsers(hash).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queries.project.pendingInvitations(hash).queryKey,
        });
        if (
          parseInt(userId, 10) === context.rootGetters['user/account/getId']
        ) {
          queryClient.invalidateQueries({
            queryKey: queries.workspace.connections.queryKey,
          });
          queryClient.invalidateQueries({
            queryKey: queries.permissions._def,
          });
        }
      });

    context.commit('join', { name: channelName });
  },

  /**
   * Activate the project collaborate channel, a presence channel. This channel receives updates on anything whispered.
   * @param {string}  hash  The project hash
   */
  startProjectCollaborateChannel(context) {
    const hash = context.rootGetters['project/getHash'];
    if (!context.rootGetters['project/users/amIParticipant']) return;
    const name = `${types.PROJECT_COLLABORATE}.${hash}`;
    if (context.getters.getIsJoined(name)) return;

    window.echo
      .join(name)
      .here(users => {
        // It will be executed immediately once the channel is joined successfully
        context.dispatch('clearCollaborators');
        users.forEach(user =>
          context.dispatch('addCollaborator', { collaborator: user })
        );
        context.dispatch('whisperAll', { hash });
      })
      .joining(user => {
        // It will be executed when a new user joins the channel
        context.dispatch('addCollaborator', { collaborator: user });
        context.dispatch('whisperAll', { hash });
      })
      .leaving(user => {
        // It will be executed when a user leaves the channel
        context.dispatch('removeCollaborator', { id: user.id });
      })
      .listenForWhisper('cursor', e => {
        const { id, pos } = e;
        context.commit('setCollaboratorCursor', { id, pos });
      })
      .listenForWhisper('board', e => {
        const { id, board } = e;
        context.commit('setCollaboratorBoard', { id, board });
      })
      .listenForWhisper('selections', e => {
        const { id, selections } = e;
        console.log('WHISPERED selections:');
        context.commit('setCollaboratorSelections', { id, selections });
      });

    context.commit('join', { name });
  },

  /**
   * Activate the user update channel. Currently only sessions of the current user are allowed to listen to it.
   * @param {Number} id   The user id
   */
  startUserUpdateChannel(context, { id }) {
    if (!context.rootGetters['user/account/getIsLoggedIn']) return;
    const name = `${types.USER_UPDATE}.${id}`;
    window.echo
      .private(name)
      .listen('UserUpdated', e => {
        console.log('UserUpdated broadcast received:', e);
        delete e.socket;
        context.dispatch('user/setAccount', { value: e }, { root: true });
      })
      .listen('UserProjectsUpdated', e => {
        const { list, open, participants } = e;
        // If the current open project is being changed leave its channels
        const currentOpen = context.rootGetters['user/projects/getOpen'];
        if (open !== currentOpen) {
          context.dispatch('leaveProjectChannels', { hash: currentOpen });
        }
        context.dispatch(
          'user/setProjects',
          { value: { list, open, participants } },
          { root: true }
        );
      })
      .listen('UserLogout', e => {
        const hash = context.rootGetters['project/getHash'];
        window.location = '/';
      })
      .listen('ParticipantUpdated', e => {
        console.log('ParticipantUpdated broadcast received:', e);
        const { participant } = e;
        context.commit(
          'user/projects/addParticipant',
          { user: participant },
          { root: true }
        );
      })
      .listen('ExportCreationUpdated', e => {
        console.log('ExportCreationUpdated broadcast received', e);
        const { filename, error } = e;
        let { progress } = e;
        if (error) {
          console.error('Export error: ', error);
          progress = null; // Abort progress
        } else {
          console.log('Progress:', progress, 'filename:', filename);
          // If file export completed, download the file
          if (progress === 100) {
            if (
              filename &&
              (filename.startsWith('http://') ||
                filename.startsWith('https://'))
            ) {
              window.location.href = filename;
              progress = null;
            } else {
              window.location.href = `/export/download/${filename}`;
              progress = null;
            }
          }
        }
        context.commit(
          'application/appStates/setExportProgress',
          { value: progress },
          { root: true }
        );
      })
      .listen('ProjectImportCompleted', e => {
        const { hash, status, message } = e;
        console.log(e);
        if (status === 'completed') {
          emitter.emit(EVENT.PROJECT_IMPORT_COMPLETED, {
            hash,
          });
          queryClient.invalidateQueries({
            queryKey: queries.workspace.projects.queryKey,
          });
        } else if (status === 'fail') {
          // TODO: Show error message
        }
      });

    context.commit('join', { name });
  },

  /**
   * Leave the project update channel
   * @param {string} hash
   */
  leaveProjectUpdateChannel(context, { hash }) {
    const name = `${types.PROJECT_UPDATE}.${hash}`;
    // Do nothing if the user has not joined this channel
    if (!context.getters.getIsJoined(name)) return;
    window.echo.leave(name);
    context.commit('leave', { name });
  },

  /**
   * Leave the project whisper channel
   * @param {string} hash
   */
  leaveProjectCollaborateChannel(context, { hash }) {
    const name = `${types.PROJECT_COLLABORATE}.${hash}`;
    // Do nothing if the user has not joined this channel
    if (!context.getters.getIsJoined(name)) return;

    context.dispatch('clearCollaborators');
    window.echo.leave(name);
    context.commit('leave', { name });
  },

  /**
   * Leave the user update channel
   * @param {Number} id
   */
  leaveUserUpdateChannel(context, { id }) {
    const name = `${types.USER_UPDATE}.${id}`;
    window.echo.leave(name);
    context.commit('leave', { name });
  },

  startWorkspaceUpdateChannel(context, { id }) {
    if (!window.echo) return;
    console.log('start workspace update channel');
    if (!context.rootGetters['user/account/getIsLoggedIn']) return;
    const name = `${types.WORKSPACE_UPDATE}.${id}`;
    if (context.getters.getIsJoined(name)) return;
    console.log('Not joined. Joinning..');
    window.echo.private(name).listen('WorkspaceUpdated', e => {
      queryClient.invalidateQueries({
        queryKey: queries.workspace._def,
      });
      queryClient.invalidateQueries({
        queryKey: queries.permissions._def,
      });
    });
    context.commit('join', { name });
  },

  leaveWorkspaceUpdateChannel(context, { id }) {
    const name = `${types.WORKSPACE_UPDATE}.${id}`;
    if (!context.getters.getIsJoined(name)) return;

    window.echo.leave(name);
    context.commit('leave', { name });
  },

  /**
   * Whisper a cursor board coordinates
   * @param {string} hash The project hash
   * @param {Object} pos  The cursor (board) coordinates
   */
  whisperCursor(context, { pos }) {
    const hash = context.rootGetters['project/getHash'];
    if (!context.rootGetters['project/users/amIParticipant']) return;

    const name = `${types.PROJECT_COLLABORATE}.${hash}`;
    const id = context.rootGetters['user/account/getId'];
    window.echo.join(name).whisper('cursor', { id, pos });
  },

  /**
   * Whisper a change in open board
   * @param {string} hash   The project hash
   * @param {string} board  The open board
   */
  whisperBoard(context, { board }) {
    const hash = context.rootGetters['project/getHash'];
    if (!context.rootGetters['project/users/amIParticipant']) return;

    const name = `${types.PROJECT_COLLABORATE}.${hash}`;
    const id = context.rootGetters['user/account/getId'];
    window.echo.join(name).whisper('board', { id, board });
  },

  /**
   * Whisper the selections
   * @param {string}  hash
   * @param {Array}   selections { elements: [], connections: [], notes: [] }
   */
  whisperSelections(context) {
    const hash = context.rootGetters['project/getHash'];
    if (!context.rootGetters['project/users/amIParticipant']) return;

    // Determine the current selection
    const selections = {
      elements: context.rootGetters['project/config/elements/getSelections'],
      connections:
        context.rootGetters['project/config/connections/getSelections'],
      notes: context.rootGetters['project/config/notes/getSelections'],
      jumpers: context.rootGetters['project/config/jumpers/getSelections'],
      branches: context.rootGetters['project/config/branches/getSelections'],
    };
    const name = `${types.PROJECT_COLLABORATE}.${hash}`;
    const id = context.rootGetters['user/account/getId'];
    window.echo.join(name).whisper('selections', { id, selections });
  },

  /**
   * Whisper everything (except cursor)
   * @param {string} hash
   */
  whisperAll(context, { hash }) {
    context.dispatch('whisperSelections', { hash });
    const board = context.rootGetters['project/config/boards/getOpen'];
    context.dispatch('whisperBoard', { hash, board });
    // const pos = { x: 9800, y: 9800 };
    // context.dispatch('whisperCursor', { hash, pos });
  },

  /**
   * Adds a collaborator other than the current user if he/she does not already exist in the collaborator list
   * @param {Object} collaborator
   */
  addCollaborator(context, { collaborator }) {
    const index = context.state.collaborators.findIndex(
      c => c.id === collaborator.id
    );
    const userId = context.rootGetters['user/account/getId'];
    if (index === -1 && collaborator.id !== userId) {
      context.commit('addCollaborator', { collaborator });
    }
  },

  /**
   * Removes a collaborator if he/she exists from the collaborator list
   * @param {number} id
   */
  removeCollaborator(context, { id }) {
    const index = context.state.collaborators.findIndex(c => c.id === id);
    if (index !== -1) {
      context.commit('removeCollaborator', { id });
    }
  },

  /**
   * Clears the collaborator list
   */
  clearCollaborators(context) {
    context.commit('clearCollaborators');
  },
};

const mutations = {
  /**
   * Join a channel
   */
  join(state, { name }) {
    state.channels.push(name);
  },

  /**
   * Leave a channel
   */
  leave(state, { name }) {
    const index = state.channels.findIndex(channel => channel === name);
    if (index !== -1) {
      state.channels.splice(index, 1);
    }
  },

  /**
   * Add a collaborator
   * @param {Object}  collaborator
   */
  addCollaborator(state, { collaborator }) {
    collaborator.cursor = null;
    collaborator.board = null;
    collaborator.selections = null;
    state.collaborators.push(collaborator);
  },

  /**
   * Remove a collaborator
   * @param {Number}  id
   */
  removeCollaborator(state, { id }) {
    const index = state.collaborators.findIndex(
      collaborator => collaborator.id === id
    );
    state.collaborators.splice(index, 1);
  },

  /**
   * Clear the collaborators list
   */
  clearCollaborators(state) {
    state.collaborators = [];
  },

  /**
   * Set a collaborator cursor
   */
  setCollaboratorCursor(state, { id, pos }) {
    const index = state.collaborators.findIndex(
      collaborator => collaborator.id === id
    );
    if (index !== -1) {
      state.collaborators[index].cursor = pos;
    }
  },

  /**
   * Set a collaborator open board
   */
  setCollaboratorBoard(state, { id, board }) {
    const index = state.collaborators.findIndex(
      collaborator => collaborator.id === id
    );
    if (index !== -1) {
      state.collaborators[index].board = board;
    }
  },

  /**
   * Set a collaborator selections
   */
  setCollaboratorSelections(state, { id, selections }) {
    const index = state.collaborators.findIndex(
      collaborator => collaborator.id === id
    );
    if (index !== -1) {
      state.collaborators[index].selections = selections;
    }
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default module;
